import { useEffect, useState } from 'react'
import { useUserPreferences } from './useUserPreferences'
import { applyTheme, getEffectiveTheme, getInitialTheme, notifyThemeChanged, onThemeChanged, setCachedTheme, type Theme } from '../lib/theme'

export type { Theme }

interface UseThemeResult {
  theme: Theme
  effectiveTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => Promise<void>
  saving: boolean
}

/**
 * Hook de gestion du thème (auto / light / dark) — spec 046 D-DarkMode-01.
 *
 * Source de vérité priorisée :
 *  1. `localStorage.theme-cache` quand l'utilisateur vient juste de choisir (application optimiste).
 *  2. `preferences.theme` retourné par l'API (= DB) en arrière-plan.
 *
 * Si l'API retourne `'auto'` (cas fallback backend tant que la migration n'est pas passée)
 * mais que le cache localStorage indique autre chose, on garde le cache : l'utilisateur
 * conserve son choix visuel jusqu'à exécution de la migration.
 *
 * Une fois la migration exécutée, l'API renvoie le vrai theme et le cache est resynchronisé.
 */
export function useTheme(): UseThemeResult {
  const { preferences, updatePreferences, saving } = useUserPreferences()

  // État local — initialisé au cache (anti-flash + résistant au fallback API)
  const [localTheme, setLocalTheme] = useState<Theme>(() => getInitialTheme())

  // Synchronise depuis l'API si la valeur retournée est explicite (≠ 'auto' OU cache vide)
  useEffect(() => {
    if (!preferences) return
    const cached = getInitialTheme()
    if (preferences.theme !== 'auto' || cached === 'auto') {
      setLocalTheme(preferences.theme)
      setCachedTheme(preferences.theme)
    }
    // Sinon (API='auto' mais cache='light'|'dark') : on garde le choix utilisateur local
  }, [preferences])

  // Applique le thème dès que `localTheme` change
  useEffect(() => {
    applyTheme(localTheme)
  }, [localTheme])

  // En mode auto, écoute les changements OS pour adapter dynamiquement
  useEffect(() => {
    if (localTheme !== 'auto') return
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('auto')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [localTheme])

  // Synchronise les autres instances de `useTheme` (Header, ProfileTab, etc.) via DOM event
  useEffect(() => {
    return onThemeChanged(next => {
      setLocalTheme(next)
    })
  }, [])

  return {
    theme: localTheme,
    effectiveTheme: getEffectiveTheme(localTheme),
    setTheme: async (next: Theme) => {
      // Application optimiste immédiate (visuel + cache) — peut survivre à un fallback API
      applyTheme(next)
      setLocalTheme(next)
      notifyThemeChanged(next)
      try {
        await updatePreferences({ theme: next })
      } catch {
        // L'erreur API n'affecte pas l'expérience visuelle (cache localStorage tient le coup)
      }
    },
    saving,
  }
}
